<template>
  <div class="exam-grading-page">
    <!-- 顶部标题栏 -->
    <div class="grading-header">
      <span class="title">数据审核</span>
      <!-- 审核级别切换（居中） -->
      <div class="review-level-tabs">
        <el-radio-group v-model="reviewLevel" size="small" @change="handleReviewLevelChange">
          <el-radio-button :label="1">一审</el-radio-button>
          <el-radio-button :label="2">二审</el-radio-button>
          <el-radio-button :label="3">三审</el-radio-button>
        </el-radio-group>
        <!-- 状态提示区域 - 固定宽度，避免布局跳动 -->
        <span class="level-hint-wrapper">
          <span class="level-hint readonly-hint" v-if="currentExaminee && isReadOnlyMode">
            <i class="el-icon-view"></i> 只读模式
          </span>
          <span class="level-hint warning" v-else-if="currentExaminee && !canEditCurrentLevel && needPreviousLevel">
            <i class="el-icon-warning"></i> 需先完成{{ previousLevelText }}审
          </span>
          <span class="level-hint warning" v-else-if="currentExaminee && !canEditCurrentLevel">
            <i class="el-icon-warning"></i> 当前级别已完成
          </span>
        </span>
      </div>
    </div>

    <!-- 三栏布局 -->
    <div class="grading-content">
      <!-- 左侧：考生列表 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">审核名单</span>
          <span class="progress">{{ gradedCount }}/{{ totalCount }}</span>
        </div>
        
        <div class="examinee-list">
          <div
            v-for="(item, index) in filteredExamineeList"
            :key="item.answerKey"
            class="examinee-item"
            :class="{ active: currentExaminee && currentExaminee.answerKey === item.answerKey }"
            @click="selectExaminee(item)"
          >
            <span class="examinee-name">{{ item.dealerCode || ('答卷' + (index + 1)) }}</span>
            <span class="status-badge" :class="getStatusClass(item)">
              {{ getStatusText(item) }}
            </span>
          </div>
        </div>
        
        <div class="filter-option">
          <el-select v-model="statusFilter" size="mini" placeholder="筛选状态" clearable style="width: 100%;">
            <el-option label="全部" value="" />
            <el-option label="待审核" :value="0" />
            <el-option label="一审完成" :value="1" />
            <el-option label="二审完成" :value="2" />
            <el-option label="三审完成" :value="3" />
          </el-select>
        </div>
        
        <div class="examinee-info" v-if="currentExaminee">
          <div class="info-row">
            <span class="label">答题时长：</span>
            <span class="value">{{ formatDuration(currentExaminee.durationSeconds) }}</span>
          </div>
          <div class="info-row">
            <span class="label">提交时间：</span>
            <span class="value">{{ currentExaminee.submitTime }}</span>
          </div>
          <div class="info-row">
            <span class="label">当前状态：</span>
            <span class="value">{{ getStatusText(currentExaminee) }}</span>
          </div>
        </div>
      </div>

      <!-- 中间：答题详情 -->
      <div class="center-panel" v-loading="detailLoading" element-loading-text="加载答题详情...">
        <div v-if="currentExaminee" class="answer-list">
          <div
            v-for="(question, qIndex) in filteredQuestionList"
            :key="question.itemKey"
            :ref="'question-' + qIndex"
            class="question-card"
          >
            <div class="question-header">
              <span class="question-title">第{{ qIndex + 1 }}题 {{ question.typeName }}</span>
              <span class="question-score-info" v-if="question.maxScore">
                ({{ question.maxScore }}分)
              </span>
            </div>
            
            <div class="question-content">
              <div class="question-label">{{ stripHtml(question.label) }}</div>
              
              <!-- 选择题选项（可点击修改选中状态） -->
              <div v-if="isChoiceQuestion(question.itemType)" class="options-list">
                <div
                  v-for="opt in question.options"
                  :key="opt.value"
                  class="option-item"
                  :class="{
                    'clickable': !isReadOnlyMode,
                    'readonly': isReadOnlyMode,
                    'is-correct': isCorrectOption(question, opt),
                    'is-original-selected': isOriginalSelected(question, opt),
                    'is-review-selected': isReviewSelected(question, opt),
                    'is-wrong': isOriginalSelected(question, opt) && !isCorrectOption(question, opt)
                  }"
                  @click="!isReadOnlyMode && toggleReviewOption(question, opt)"
                >
                  <span class="option-marker">
                    <!-- 审核选中状态 -->
                    <i v-if="isReviewSelected(question, opt)" class="el-icon-check review-check"></i>
                    <!-- 正确答案标记 -->
                    <i v-else-if="isCorrectOption(question, opt)" class="el-icon-success correct-icon"></i>
                    <!-- 未选中：多选题用方框，单选题用圆圈 -->
                    <span v-else-if="isMultipleChoice(question.itemType)" class="option-checkbox"></span>
                    <span v-else class="option-circle"></span>
                  </span>
                  <span class="option-text">{{ stripHtml(opt.label) }}</span>
                  <span v-if="isOriginalSelected(question, opt)" class="original-tag">原答案</span>
                  <span v-if="isReview1Selected(question, opt)" class="review1-tag">一审</span>
                  <span v-if="isReview2Selected(question, opt)" class="review2-tag">二审</span>
                </div>
              </div>
              
              <!-- 主观题答案 -->
              <div v-else class="subjective-answer">
                <div class="answer-label">考生答案：</div>
                <div class="answer-content">{{ getAnswerValue(question.itemKey) || '未作答' }}</div>
              </div>
            </div>
            
            <!-- 审核评分区域 -->
            <div class="review-section">
              <div class="review-options">
                <span class="review-label">评审结果：</span>
                <el-radio-group 
                  v-model="reviewMap[question.itemKey]" 
                  size="small" 
                  @change="handleReviewChange(question, $event)"
                  :disabled="isReadOnlyMode"
                >
                  <el-radio label="SCORE" :disabled="!canSelectScore(question)">
                    <i class="el-icon-check" style="color: #67C23A"></i> 得分
                  </el-radio>
                  <el-radio label="NO_SCORE">
                    <i class="el-icon-close" style="color: #F56C6C"></i> 不得分
                  </el-radio>
                  <el-radio label="NOT_APPLICABLE" :disabled="!canSelectNotApplicable(question)">
                    <i class="el-icon-minus" style="color: #909399"></i> 不涉及
                  </el-radio>
                </el-radio-group>
              </div>
              <div class="review-remark">
                <el-input
                  type="textarea"
                  v-model="remarkMap[question.itemKey]"
                  placeholder="备注（可选）"
                  :rows="2"
                  maxlength="500"
                  show-word-limit
                  size="small"
                  :disabled="isReadOnlyMode"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-tip">
          <p>请从左侧选择一份试卷进行阅卷</p>
        </div>
      </div>

      <!-- 右侧：评分区域 -->
      <div class="right-panel">
        <div class="score-summary">
          <span class="label">原始得分</span>
          <span class="score original">{{ originalScore }}</span>
        </div>
        
        <div class="score-summary review-score">
          <span class="label">审核得分</span>
          <span class="score" :class="{ changed: reviewScoreChanged }">{{ reviewScore }}</span>
        </div>
        
        <div class="level-display" v-if="scoreLevel">
          <span class="level-label">等级</span>
          <span class="level-value" :style="{ color: scoreLevel.color }">{{ scoreLevel.name }}</span>
        </div>
        
        <!-- 题目导航卡片 -->
        <div class="question-nav">
          <div
            v-for="(q, i) in filteredQuestionList"
            :key="q.itemKey"
            class="nav-item"
            :class="getNavItemClass(q)"
            @click="scrollToQuestion(i)"
          >
            {{ i + 1 }}
          </div>
        </div>
        
        <!-- 切换按钮 -->
        <div class="nav-buttons">
          <el-button size="small" :disabled="currentIndex <= 0" @click="prevExaminee">上一份</el-button>
          <el-button size="small" :disabled="currentIndex >= filteredExamineeList.length - 1" @click="nextExaminee">下一份</el-button>
        </div>
      </div>
    </div>
    
    <!-- 底部固定按钮栏 -->
    <div class="grading-footer">
      <el-button 
        v-if="!isReadOnlyMode"
        type="success" 
        size="medium" 
        @click="handleSubmitReview" 
        :disabled="!canEditCurrentLevel"
      >
        <i class="el-icon-check"></i> 提交{{ reviewLevelText }}审核
      </el-button>
      <span v-else class="readonly-text">
        <i class="el-icon-view"></i> 当前为只读模式，仅供查看
      </span>
      <el-button size="medium" @click="handleSaveAndBack">
        <i class="el-icon-back"></i> 返回
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExamGrading',
  props: {
    formKey: {
      type: String,
      required: true
    },
    projectType: {
      type: Number,
      default: 0
    },
    // 从数据页面选中进入时，指定默认选中的考生
    initialAnswerKey: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      examineeList: [],
      currentExaminee: null,
      currentIndex: -1,
      questionList: [],
      answerMap: {},        // itemKey -> 用户原始答案
      reviewAnswerMap: {},  // itemKey -> 审核人修改后的答案（当前审核级别）
      review1AnswerMap: {}, // itemKey -> 一审修改后的答案（二审/三审时使用）
      review2AnswerMap: {}, // itemKey -> 二审修改后的答案（三审时使用）
      reviewMap: {},        // itemKey -> SCORE/NO_SCORE/NOT_APPLICABLE
      remarkMap: {},        // itemKey -> remark text
      reviewLevel: 1,       // 当前审核级别 1/2/3
      statusFilter: '',     // 状态筛选
      loading: false,
      detailLoading: false, // 答题详情加载中
      updateCounter: 0,     // 用于强制计算属性重新计算
      answerCache: {},      // answerKey -> { answers: [], reviewDetail: {} }
      preloadQueue: []      // 预加载队列
    }
  },
  computed: {
    totalCount() {
      return this.examineeList.length
    },
    gradedCount() {
      return this.examineeList.filter(e => e.auditStatus >= this.reviewLevel).length
    },
    filteredExamineeList() {
      if (this.statusFilter === '' || this.statusFilter === null) {
        return this.examineeList
      }
      return this.examineeList.filter(e => e.auditStatus === this.statusFilter)
    },
    // 只显示考生实际作答的题目（根据answerMap筛选）
    filteredQuestionList() {
      if (!this.answerMap || Object.keys(this.answerMap).length === 0) {
        // 如果没有答题记录，显示全部题目
        return this.questionList
      }
      // 筛选出有答题记录的题目
      return this.questionList.filter(q => this.answerMap.hasOwnProperty(q.itemKey))
    },
    // 原始得分（考生答题后系统自动计算的）
    originalScore() {
      if (this.currentExaminee && this.currentExaminee.totalScore !== undefined && this.currentExaminee.totalScore !== null) {
        return parseFloat(this.currentExaminee.totalScore).toFixed(1)
      }
      return '0.0'
    },
    // 审核得分（根据当前评审结果实时计算）
    reviewScore() {
      // 依赖 updateCounter 确保响应式更新
      const _trigger = this.updateCounter
      let sum = 0
      for (const q of this.filteredQuestionList) {
        const result = this.reviewMap[q.itemKey]
        if (result === 'SCORE') {
          // 对于 perOption 模式，计算实际选中选项的分值
          if (q.scoringMethod === 'perOption' && this.hasAllOptionsWithScore(q)) {
            const reviewAnswer = this.reviewAnswerMap[q.itemKey]
            if (reviewAnswer) {
              // 解析选中的值
              let selectedValues = []
              try {
                const parsed = JSON.parse(reviewAnswer)
                if (Array.isArray(parsed)) {
                  selectedValues = parsed.map(String)
                } else {
                  selectedValues = [String(parsed)]
                }
              } catch {
                // 非 JSON 格式，直接使用原始值
                selectedValues = [String(reviewAnswer)]
              }
              
              // 计算选中选项的总分
              selectedValues.forEach(val => {
                // 尝试多种方式匹配选项
                let opt = q.options.find(o => String(o.value) === val)
                if (!opt) {
                  // 尝试去除 HTML 标签后匹配
                  opt = q.options.find(o => this.stripHtml(o.value) === val || this.stripHtml(o.label) === val)
                }
                if (opt && opt.score) {
                  sum += opt.score
                }
              })
            }
          } else {
            // 正确答案模式或其他模式，使用题目满分
            sum += (q.maxScore || 0)
          }
        }
      }
      return sum.toFixed(1)
    },
    // 审核得分是否与原始得分不同
    reviewScoreChanged() {
      return this.reviewScore !== this.originalScore && this.reviewScore !== '0.0'
    },
    scoreLevel() {
      // 使用审核得分来计算等级（如果有审核结果），否则使用原始得分
      const score = this.reviewScoreChanged ? parseFloat(this.reviewScore) : parseFloat(this.originalScore)
      if (score >= 90) return { name: '优秀', color: '#67C23A' }
      if (score >= 75) return { name: '良好', color: '#409EFF' }
      if (score >= 60) return { name: '及格', color: '#E6A23C' }
      return { name: '不及格', color: '#F56C6C' }
    },
    // 当前级别文本
    reviewLevelText() {
      const texts = { 1: '一', 2: '二', 3: '三' }
      return texts[this.reviewLevel] || ''
    },
    // 当前级别是否可编辑
    canEditCurrentLevel() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 只有当前级别等于审核状态时才可编辑（正在进行中的级别）
      return this.reviewLevel === status + 1 || (status === 0 && this.reviewLevel === 1)
    },
    // 是否是只读模式（查看已完成的审核级别）
    isReadOnlyMode() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 如果当前查看的级别小于等于已完成的级别，是只读模式
      return this.reviewLevel <= status
    },
    // 是否需要先完成上级审核（选择的级别比当前状态高出超过1级）
    needPreviousLevel() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 如果当前选择的级别比已完成的状态高出超过1级，需要先完成上级
      return this.reviewLevel > status + 1
    },
    // 需要完成哪一级审核的文本
    previousLevelText() {
      const texts = { 1: '一', 2: '二', 3: '三' }
      const status = this.currentExaminee?.auditStatus || 0
      return texts[status + 1] || '一'
    }
  },
  created() {
    this.loadExamineeList()
    this.loadQuestions()
  },
  mounted() {
    // 调整父容器样式，让阅卷页面填满整个可视区域（减去顶部导航60px）
    const dataPage = this.$el.closest('.data-page')
    if (dataPage) {
      this._originalHeight = dataPage.style.height
      this._originalBackground = dataPage.style.background
      dataPage.style.height = 'calc(100vh - 60px)'
      dataPage.style.background = '#f5f7fa'
    }
  },
  beforeDestroy() {
    // 恢复父容器原始样式
    const dataPage = this.$el.closest('.data-page')
    if (dataPage) {
      if (this._originalHeight !== undefined) {
        dataPage.style.height = this._originalHeight
      }
      if (this._originalBackground !== undefined) {
        dataPage.style.background = this._originalBackground
      }
    }
  },
  methods: {
    async loadExamineeList() {
      try {
        this.loading = true
        const { getAnswerPage } = await import('index/api/project/form')
        // 增大 size 上限，只加载基本信息（answers 在选中时加载）
        const res = await getAnswerPage({ formKey: this.formKey, page: 1, size: 500 })
        const data = res.data?.data || res.data || {}
        const records = data.records || data.list || []
        
        this.examineeList = records.map(r => ({
          answerKey: r.answerKey,
          submitTime: r.submitTime,
          durationSeconds: r.durationSeconds,
          totalScore: r.totalScore,
          reviewedScore: r.reviewedScore,
          auditStatus: r.auditStatus || 0,
          dealerCode: r.dealerCode || r.DealerCode || null
          // 注意：不再在列表加载时存储 answers，改为选中时加载
        }))
        
        // 如果后端返回了 answers，缓存起来
        records.forEach(r => {
          if (r.answers && r.answers.length > 0) {
            this.answerCache[r.answerKey] = { answers: r.answers }
          }
        })
        
        this.loading = false
        
        if (this.examineeList.length > 0) {
          if (this.initialAnswerKey) {
            const targetExaminee = this.examineeList.find(e => e.answerKey === this.initialAnswerKey)
            if (targetExaminee) {
              this.selectExaminee(targetExaminee)
            } else {
              this.selectExaminee(this.examineeList[0])
            }
          } else {
            this.selectExaminee(this.examineeList[0])
          }
        }
      } catch (e) {
        this.loading = false
        console.error('[ExamGrading] Load examinee list failed:', e)
      }
    },
    
    async loadQuestions() {
      try {
        const { getFormItems } = await import('index/api/project/form')
        const res = await getFormItems(this.formKey)
        const items = res.data?.data || res.data || []
        
        this.questionList = items.filter(item => !this.isPaginationItem(item.itemType)).map(item => {
          // 解析 config_json 获取配置
          let config = {}
          try {
            const configStr = item.configJson || item.config_json || '{}'
            config = JSON.parse(configStr)
          } catch (e) {
            console.warn('[loadQuestions] 解析 config_json 失败:', e)
          }
          
          // 获取选项：优先使用 options，兼容不同字段名
          const options = item.options || item.optionsRaw || []
          
          // 尝试从多个来源获取分数
          let scoreValue = item.score || item.maxScore || config.score || 0
          
          // 如果没有直接的分数字段，尝试从选项中获取最大分数
          if (!scoreValue && options.length > 0) {
            const optionScores = options.map(opt => opt.score || 0)
            scoreValue = Math.max(...optionScores)
          }
          
          // 如果还是0，使用默认值10分
          if (!scoreValue) {
            scoreValue = 10
          }
          
          // 获取正确答案：优先从 config_json 中获取
          const correctAnswer = item.correctAnswer || config.correctAnswer || null
          
          // 打印完整数据结构便于调试
          console.log('[loadQuestions] 完整数据:', {
            itemKey: item.itemKey,
            itemType: item.itemType,
            scoringMethod: config.scoringMethod,
            correctAnswer,
            options: options.map(o => ({ label: o.label?.substring(0, 15), score: o.score }))
          })
          
          return {
            itemKey: item.itemKey,
            itemType: item.itemType,
            typeName: this.getTypeName(item.itemType),
            label: item.label || item.title,
            options: options,
            correctAnswer: correctAnswer,
            maxScore: scoreValue,
            configJson: item.configJson || item.config_json || '{}',
            description: item.description || '',
            tipContent: item.tipContent || item.tip_content || '',
            scoringMethod: config.scoringMethod || 'single',
            enableScoring: config.enableScoring,
            exclusive: config.exclusive || config.__config__?.exclusive || false,
            exclusiveOptions: config.exclusiveOptions || config.__config__?.exclusiveOptions || []
          }
        })
      } catch (e) {
        console.error('[ExamGrading] Load questions failed:', e)
      }
    },
    
    async selectExaminee(examinee) {
      this.currentExaminee = examinee
      this.currentIndex = this.filteredExamineeList.findIndex(e => e.answerKey === examinee.answerKey)
      
      // 重置答案映射
      this.answerMap = {}
      this.reviewAnswerMap = {}
      this.reviewMap = {}
      this.remarkMap = {}
      this.review1AnswerMap = {}
      this.review2AnswerMap = {}
      
      const answerKey = examinee.answerKey
      
      // 从缓存加载答题详情，或发起请求
      let answers = []
      let reviewDetail = null
      
      if (this.answerCache[answerKey]) {
        // 命中缓存
        answers = this.answerCache[answerKey].answers || []
        reviewDetail = this.answerCache[answerKey].reviewDetail
      } else {
        // 需要加载
        this.detailLoading = true
        try {
          const { getAnswerDetail } = await import('index/api/project/form')
          const res = await getAnswerDetail(answerKey)
          const data = res.data?.data || res.data || {}
          answers = data.answers || []
          // 缓存答题详情
          this.answerCache[answerKey] = { answers }
        } catch (e) {
          console.warn('[ExamGrading] Load answer detail failed:', e)
        }
        this.detailLoading = false
      }
      
      // 构建答案映射
      answers.forEach(ans => {
        this.answerMap[ans.itemKey] = ans.value
        this.reviewAnswerMap[ans.itemKey] = ans.value
      })
      
      // 加载审核详情（带缓存）
      if (reviewDetail) {
        this.applyReviewDetail(reviewDetail)
      } else {
        try {
          const { getReviewDetail } = await import('index/api/project/form')
          const res = await getReviewDetail(answerKey)
          const data = res.data?.data || res.data || {}
          // 缓存审核详情
          if (this.answerCache[answerKey]) {
            this.answerCache[answerKey].reviewDetail = data
          }
          this.applyReviewDetail(data)
        } catch (e) {
          console.error('[ExamGrading] Load review detail failed:', e)
        }
      }
      
      // 初始化未设置的题目（自动根据答题结果判断得分/不得分）
      this.questionList.forEach(q => {
        if (!(q.itemKey in this.reviewMap) || !this.reviewMap[q.itemKey]) {
          const autoResult = this.autoCheckAnswerResult(q)
          this.$set(this.reviewMap, q.itemKey, autoResult)
        }
        if (!(q.itemKey in this.remarkMap)) {
          this.$set(this.remarkMap, q.itemKey, '')
        }
      })
      
      // 预加载相邻的项（后台静默加载）
      this.preloadAdjacent()
    },
    
    // 应用审核详情数据
    applyReviewDetail(data) {
      if (!data.items) return
      
      data.items.forEach(item => {
        // 加载一审答案
        if (item.reviewAnswer1) {
          this.$set(this.review1AnswerMap, item.itemKey, item.reviewAnswer1)
        }
        // 加载二审答案
        if (item.reviewAnswer2) {
          this.$set(this.review2AnswerMap, item.itemKey, item.reviewAnswer2)
        }
        
        // 根据审核级别获取对应级别的结果、答案和备注
        let result, answer, remark
        if (this.reviewLevel === 3) {
          result = item.reviewResult3
          answer = item.reviewAnswer3 || item.reviewAnswer2 || item.reviewAnswer1
          remark = item.reviewRemark3 || item.reviewRemark2 || item.reviewRemark1
        } else if (this.reviewLevel === 2) {
          result = item.reviewResult2
          answer = item.reviewAnswer2 || item.reviewAnswer1
          remark = item.reviewRemark2 || item.reviewRemark1
        } else {
          result = item.reviewResult1
          answer = item.reviewAnswer1
          remark = item.reviewRemark1
        }
        
        if (result) {
          this.$set(this.reviewMap, item.itemKey, result)
        }
        if (answer) {
          this.$set(this.reviewAnswerMap, item.itemKey, answer)
        }
        if (remark) {
          this.$set(this.remarkMap, item.itemKey, remark)
        }
      })
    },
    
    // 预加载相邻的考生详情（静默后台加载）
    async preloadAdjacent() {
      const list = this.filteredExamineeList
      const idx = this.currentIndex
      const preloadRange = 2 // 预加载前后各2个
      
      for (let offset = 1; offset <= preloadRange; offset++) {
        // 预加载后面的项
        if (idx + offset < list.length) {
          this.preloadExamineeDetail(list[idx + offset])
        }
        // 预加载前面的项
        if (idx - offset >= 0) {
          this.preloadExamineeDetail(list[idx - offset])
        }
      }
    },
    
    // 预加载单个考生的详情（静默，不显示 loading）
    async preloadExamineeDetail(examinee) {
      const answerKey = examinee.answerKey
      if (this.answerCache[answerKey]) return // 已缓存
      
      try {
        const { getAnswerDetail } = await import('index/api/project/form')
        const res = await getAnswerDetail(answerKey)
        const data = res.data?.data || res.data || {}
        this.answerCache[answerKey] = { answers: data.answers || [] }
        
        // 同时预加载审核详情
        const { getReviewDetail } = await import('index/api/project/form')
        const reviewRes = await getReviewDetail(answerKey)
        const reviewData = reviewRes.data?.data || reviewRes.data || {}
        this.answerCache[answerKey].reviewDetail = reviewData
      } catch (e) {
        // 预加载失败不影响正常使用
      }
    },
    
    handleReviewLevelChange() {
      // 切换审核级别时重新加载当前试卷
      if (this.currentExaminee) {
        this.selectExaminee(this.currentExaminee)
      }
    },
    
    prevExaminee() {
      if (this.currentIndex > 0) {
        this.selectExaminee(this.filteredExamineeList[this.currentIndex - 1])
      }
    },
    
    nextExaminee() {
      if (this.currentIndex < this.filteredExamineeList.length - 1) {
        this.selectExaminee(this.filteredExamineeList[this.currentIndex + 1])
      }
    },
    
    async handleSaveAndBack() {
      this.$emit('back')
    },
    
    // 评审结果变更处理（强制触发响应式更新）
    handleReviewChange(question, value) {
      const itemKey = question.itemKey
      const currentResult = this.reviewMap[itemKey]
      
      // 如果当前是得分状态，不允许改为不涉及
      if (currentResult === 'SCORE' && value === 'NOT_APPLICABLE') {
        this.$message.warning('得分选项不能修改为不涉及')
        // 强制恢复原值
        this.$nextTick(() => {
          this.$set(this.reviewMap, itemKey, 'SCORE')
        })
        return
      }
      
      // 如果尝试选择得分但不满足条件，阻止
      if (value === 'SCORE' && !this.canSelectScore(question)) {
        this.$message.warning('只有选中正确答案才能选择得分')
        this.$nextTick(() => {
          this.$set(this.reviewMap, itemKey, currentResult || 'NO_SCORE')
        })
        return
      }
      
      // 使用 $set 强制触发响应式更新
      this.$set(this.reviewMap, itemKey, value)
    },
    
    // 判断是否可以选择"得分"
    canSelectScore(question) {
      // 检查是否所有选项都有分值设置
      if (this.hasAllOptionsWithScore(question)) {
        return true  // 每个选项都有分值，都可以得分
      }
      
      // 否则需要判断当前审核答案是否正确
      const reviewAnswer = this.reviewAnswerMap[question.itemKey]
      if (!reviewAnswer) return false
      
      if (!question.correctAnswer) {
        return true  // 没有正确答案的题目，默认可以得分
      }
      
      return this.checkAnswerCorrect(reviewAnswer, question.correctAnswer)
    },
    
    // 判断是否可以选择"不涉及"
    canSelectNotApplicable(question) {
      const currentResult = this.reviewMap[question.itemKey]
      // 如果当前是得分状态，不允许改为不涉及
      if (currentResult === 'SCORE') {
        return false
      }
      // 不得分状态可以改为不涉及
      return true
    },
    
    // 判断是否所有选项都设置了分值
    hasAllOptionsWithScore(question) {
      if (!question.options || question.options.length === 0) {
        return false
      }
      // 添加调试日志
      console.log('[hasAllOptionsWithScore] 题目:', question.itemKey, '选项:', question.options.map(opt => ({
        value: opt.value,
        label: opt.label?.substring(0, 20),
        score: opt.score
      })))
      // 检查每个选项是否都有分值
      const result = question.options.every(opt => opt.score !== undefined && opt.score !== null && opt.score > 0)
      console.log('[hasAllOptionsWithScore] 结果:', result)
      return result
    },
    
    // 判断某一级别是否可编辑
    canEditLevel(level) {
      if (!this.currentExaminee) return true  // 没选择试卷时都可点击
      const status = this.currentExaminee.auditStatus || 0
      // 允许编辑当前状态及之后的级别（例如：一审完成时，可以修改一审；只有进入二审后才不能修改一审）
      return level >= status
    },
    
    // 提交当前级别的审核
    async handleSubmitReview() {
      if (!this.currentExaminee) {
        this.$message.warning('请先选择试卷')
        return
      }
      
      if (!this.canEditCurrentLevel) {
        this.$message.warning('当前级别已完成审核，不可修改')
        return
      }
      
      // 检查是否所有题目都已评审
      const unreviewed = this.filteredQuestionList.filter(q => !this.reviewMap[q.itemKey])
      if (unreviewed.length > 0) {
        this.$message.warning(`还有 ${unreviewed.length} 道题目未评审`)
        return
      }
      
      try {
        // 构建审核结果（包含修改后的答案）
        const results = Object.entries(this.reviewMap).map(([itemKey, result]) => ({
          ItemKey: itemKey,
          Result: result,
          Answer: this.reviewAnswerMap[itemKey] || '',  // 发送修改后的答案
          Remark: this.remarkMap[itemKey] || ''
        }))
        
        const { submitReview } = await import('index/api/project/form')
        await submitReview({
          AnswerKey: this.currentExaminee.answerKey,
          ReviewLevel: this.reviewLevel,
          Reviewer: 'Admin', // TODO: 从用户信息获取
          Results: results
        })
        
        // 更新当前试卷的审核状态
        this.currentExaminee.auditStatus = this.reviewLevel
        // 同时更新列表中的状态（确保列表显示正确）
        const idx = this.examineeList.findIndex(e => e.answerKey === this.currentExaminee.answerKey)
        if (idx >= 0) {
          this.$set(this.examineeList[idx], 'auditStatus', this.reviewLevel)
        }
        
        // 清除该考生的缓存，确保下次加载时获取最新的审核详情
        if (this.answerCache[this.currentExaminee.answerKey]) {
          delete this.answerCache[this.currentExaminee.answerKey].reviewDetail
        }
        
        this.$message.success(`${this.reviewLevelText}审提交成功`)
        
        // 自动切换到下一份试卷
        if (this.currentIndex < this.filteredExamineeList.length - 1) {
          this.nextExaminee()
        }
      } catch (e) {
        console.error('[ExamGrading] Submit review failed:', e)
        this.$message.error('提交失败')
      }
    },
    
    getStatusClass(examinee) {
      const status = examinee.auditStatus || 0
      if (status >= 3) return 'status-complete'
      if (status >= 2) return 'status-review2'
      if (status >= 1) return 'status-review1'
      return 'status-pending'
    },
    
    getStatusText(examinee) {
      const status = examinee.auditStatus || 0
      if (status >= 3) return '三审完成'
      if (status >= 2) return '二审完成'
      if (status >= 1) return '一审完成'
      return '待审核'
    },
    
    isPaginationItem(type) {
      return ['PAGINATION', 'PAGE_BREAK', 'DIVIDER', 'DESCRIPTION'].includes(type)
    },
    
    getTypeName(type) {
      const map = {
        'RADIO': '单选题',
        'CHECKBOX': '多选题',
        'SINGLE_CHOICE': '单选题',
        'MULTIPLE_CHOICE': '多选题',
        'SELECT': '下拉选择',
        'TEXTAREA': '多行文本',
        'INPUT': '单行文本',
        'FILL_BLANK': '填空题'
      }
      return map[type] || type
    },
    
    isChoiceQuestion(type) {
      return ['RADIO', 'CHECKBOX', 'SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'SELECT'].includes(type)
    },
    
    // 是否是多选题
    isMultipleChoice(type) {
      return ['CHECKBOX', 'MULTIPLE_CHOICE'].includes(type)
    },
    
    isCorrectOption(question, option) {
      if (!question.correctAnswer) return false
      try {
        const correct = JSON.parse(question.correctAnswer)
        if (Array.isArray(correct)) {
          return correct.includes(option.value)
        }
        return correct === option.value
      } catch {
        return String(question.correctAnswer) === String(option.value)
      }
    },
    
    // 是否是用户原始选中的选项
    isOriginalSelected(question, option) {
      const answer = this.answerMap[question.itemKey]
      if (!answer) return false
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.includes(option.value)
        }
        return selected === option.value
      } catch {
        return String(answer) === String(option.value)
      }
    },
    
    // 是否是一审选中的选项（二审/三审时显示）
    isReview1Selected(question, option) {
      if (this.reviewLevel < 2) return false
      const answer = this.review1AnswerMap[question.itemKey]
      if (!answer) return false
      return this.isAnswerMatchOption(answer, option)
    },
    
    // 是否是二审选中的选项（三审时显示）
    isReview2Selected(question, option) {
      if (this.reviewLevel < 3) return false
      const answer = this.review2AnswerMap[question.itemKey]
      if (!answer) return false
      return this.isAnswerMatchOption(answer, option)
    },
    
    // 辅助方法：判断答案是否匹配选项
    isAnswerMatchOption(answer, option) {
      try {
        const selected = JSON.parse(answer)
        const optValue = String(option.value)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue)
        }
        return String(selected) === optValue
      } catch {
        return String(answer) === String(option.value) || 
               this.stripHtml(answer) === this.stripHtml(option.value)
      }
    },
    
    // 是否是审核人选中的选项
    isReviewSelected(question, option) {
      const reviewAnswer = this.reviewAnswerMap[question.itemKey]
      if (!reviewAnswer) return false
      try {
        const selected = JSON.parse(reviewAnswer)
        const optValue = String(option.value)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue)
        }
        return String(selected) === optValue
      } catch {
        return String(reviewAnswer) === String(option.value)
      }
    },
    
    // 格式化题目数据为 Question 组件期望的格式
    formatQuestionItem(question) {
      return {
        item_key: question.itemKey,
        item_type: question.itemType,
        label: question.label,
        description: question.description || '',
        tip_content: question.tipContent || '',
        config_json: question.configJson || '{}',
        options: question.options
      }
    },
    
    // 获取审核答案（优先显示审核修改后的答案）
    getReviewAnswer(itemKey) {
      return this.reviewAnswerMap[itemKey] || this.answerMap[itemKey] || null
    },
    
    // 获取选项标签文本
    getOptionLabel(opt) {
      if (!opt) return ''
      return this.stripHtml(opt.label || opt.text || String(opt.value))
    },
    
    // 处理 Question 组件的答案变更
    handleQuestionChange(payload) {
      const { itemKey, value } = payload
      this.$set(this.reviewAnswerMap, itemKey, value)
      // 找到对应的 question 对象并更新评审结果
      const question = this.questionList.find(q => q.itemKey === itemKey)
      if (question) {
        this.updateReviewResultFromAnswer(question, value)
      }
    },

    // 点击切换审核选项
    toggleReviewOption(question, option) {
      const isMultiple = question.itemType === 'CHECKBOX' || question.itemType === 'MULTIPLE_CHOICE'
      let current = this.reviewAnswerMap[question.itemKey] || ''
      let selected = []
      
      // 解析当前选中状态 - 确保正确解析字符串类型
      if (current) {
        try {
          const parsed = JSON.parse(current)
          if (Array.isArray(parsed)) {
            selected = parsed.map(String)
          } else {
            selected = [String(parsed)]
          }
        } catch {
          selected = current.split(',').map(s => s.trim()).filter(Boolean)
        }
      }
      
      const optValue = String(option.value)
      // 使用findIndex并确保类型一致比较
      const idx = selected.findIndex(v => String(v) === optValue)
      
      console.log('[toggleReviewOption] current:', current, 'selected:', selected, 'optValue:', optValue, 'idx:', idx)
      
      if (isMultiple) {
        // 多选：切换选中状态
        if (idx >= 0) {
          selected.splice(idx, 1)
        } else {
          selected.push(optValue)
        }
        
        // 应用互斥逻辑
        if (question.exclusive && question.exclusiveOptions && question.exclusiveOptions.length > 0) {
          const exclusiveOptStrs = question.exclusiveOptions.map(v => String(v))
          const clickedIsExclusive = exclusiveOptStrs.includes(optValue)
          const wasExclusiveSelected = selected.filter(v => v !== optValue).some(v => exclusiveOptStrs.includes(v))
          
          if (clickedIsExclusive && idx < 0) {
            // 刚刚选中了互斥选项 -> 只保留该互斥选项，取消其他所有
            selected = [optValue]
            console.log('[toggleReviewOption] 选中互斥选项，清除其他选项')
          } else if (!clickedIsExclusive && wasExclusiveSelected) {
            // 在互斥选项已选中的情况下选了其他 -> 取消所有互斥选项
            selected = selected.filter(v => !exclusiveOptStrs.includes(v))
            console.log('[toggleReviewOption] 取消所有互斥选项')
          }
        }
      } else {
        // 单选：如果已选中则取消，否则选中新选项
        if (idx >= 0) {
          selected = []
        } else {
          selected = [optValue]
        }
      }
      
      // 保存新的审核答案
      const newAnswer = selected.length > 0 ? JSON.stringify(selected) : ''
      
      // 使用 $set 确保响应式更新
      this.$set(this.reviewAnswerMap, question.itemKey, newAnswer)
      
      // 递增计数器强制计算属性重新计算
      this.updateCounter++
      
      // 自动更新评审结果
      this.updateReviewResultFromAnswer(question, newAnswer)
    },
    
    // 根据审核答案自动更新评审结果
    updateReviewResultFromAnswer(question, reviewAnswer) {
      if (!reviewAnswer) {
        // 没有选择任何选项，不得分
        this.$set(this.reviewMap, question.itemKey, 'NO_SCORE')
        return
      }
      
      // 情况1: 如果每个选项都设置了分值，则选中有分值的选项即得分
      if (this.hasAllOptionsWithScore(question)) {
        // 计算选中选项的总分值
        let selectedScore = 0
        try {
          let selectedValues = JSON.parse(reviewAnswer)
          if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              selectedScore += opt.score
            }
          })
        } catch {
          // 解析失败，按不得分处理
        }
        
        // 有选中且有分值就得分
        this.$set(this.reviewMap, question.itemKey, selectedScore > 0 ? 'SCORE' : 'NO_SCORE')
        return
      }
      
      // 情况2: 有正确答案，比对审核答案与正确答案
      if (question.correctAnswer) {
        const isCorrect = this.checkAnswerCorrect(reviewAnswer, question.correctAnswer)
        this.$set(this.reviewMap, question.itemKey, isCorrect ? 'SCORE' : 'NO_SCORE')
      } else {
        // 情况3: 没有正确答案的题目，不自动设置得分，需要审核人员手动判断
        // 如果当前已有评审结果则保持，否则默认不得分
        const currentResult = this.reviewMap[question.itemKey]
        if (!currentResult) {
          this.$set(this.reviewMap, question.itemKey, 'NO_SCORE')
        }
        // 否则保持原评审结果不变，让用户手动修改
      }
    },
    
    isSelectedOption(question, option) {
      const answer = this.answerMap[question.itemKey]
      if (!answer) return false
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.includes(option.value)
        }
        return selected === option.value
      } catch {
        return String(answer) === String(option.value)
      }
    },
    
    getAnswerValue(itemKey) {
      return this.answerMap[itemKey] || ''
    },
    
    getNavItemClass(question) {
      const result = this.reviewMap[question.itemKey]
      if (!result) return 'unanswered'
      if (result === 'SCORE') return 'correct'
      if (result === 'NO_SCORE') return 'wrong'
      if (result === 'NOT_APPLICABLE') return 'not-applicable'
      return 'unanswered'
    },
    
    scrollToQuestion(index) {
      const ref = this.$refs['question-' + index]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    
    formatDuration(seconds) {
      if (!seconds) return '00:00:00'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    
    stripHtml(str) {
      if (!str) return ''
      return str.replace(/<[^>]+>/g, '').trim()
    },
    
    // 自动检查答案是否正确，返回 SCORE/NO_SCORE
    autoCheckAnswerResult(question) {
      const userAnswer = this.answerMap[question.itemKey]
      
      console.log('[autoCheckAnswerResult]', {
        itemKey: question.itemKey,
        itemType: question.itemType,
        userAnswer,
        correctAnswer: question.correctAnswer,
        hasCorrectAnswer: !!question.correctAnswer,
        scoringMethod: question.scoringMethod,
        hasAllOptionsWithScore: this.hasAllOptionsWithScore(question)
      })
      
      // 如果没有作答，返回不得分
      if (!userAnswer) {
        console.log('[autoCheckAnswerResult] 没有作答，返回 NO_SCORE')
        return 'NO_SCORE'
      }
      
      // 情况1：每个选项都设置了分值（perOption 模式），选中有分值的选项即得分
      if (this.hasAllOptionsWithScore(question)) {
        console.log('[autoCheckAnswerResult] 每个选项都有分值模式，检查选中项分值')
        // 计算选中选项的分值
        let selectedScore = 0
        try {
          let selectedValues = []
          try {
            selectedValues = JSON.parse(userAnswer)
            if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          } catch {
            selectedValues = [userAnswer]
          }
          
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              selectedScore += opt.score
            }
          })
        } catch (e) {
          console.warn('[autoCheckAnswerResult] 计算分值失败:', e)
        }
        
        console.log('[autoCheckAnswerResult] 选中项分值:', selectedScore)
        return selectedScore > 0 ? 'SCORE' : 'NO_SCORE'
      }
      
      // 情况2：有正确答案，根据正确答案判断
      if (question.correctAnswer) {
        const isCorrect = this.checkAnswerCorrect(userAnswer, question.correctAnswer)
        console.log('[autoCheckAnswerResult] 有正确答案，判断结果:', isCorrect)
        return isCorrect ? 'SCORE' : 'NO_SCORE'
      }
      
      // 情况3：没有正确答案也没有选项分值，需要人工判断
      console.log('[autoCheckAnswerResult] 需要人工判断')
      return ''  // 需要人工判断
    },
    
    // 比较用户答案和正确答案是否匹配
    checkAnswerCorrect(userAnswer, correctAnswer) {
      console.log('[checkAnswerCorrect] 输入:', { userAnswer, correctAnswer })
      
      if (!userAnswer || !correctAnswer) {
        console.log('[checkAnswerCorrect] 缺少答案，返回 false')
        return false
      }
      
      try {
        // 尝试解析为数组（多选题）
        let userArr = []
        let correctArr = []
        
        try {
          userArr = JSON.parse(userAnswer)
          if (!Array.isArray(userArr)) userArr = [userArr]
        } catch {
          userArr = String(userAnswer).split(',').map(s => s.trim())
        }
        
        try {
          correctArr = JSON.parse(correctAnswer)
          if (!Array.isArray(correctArr)) correctArr = [correctArr]
        } catch {
          correctArr = String(correctAnswer).split(',').map(s => s.trim())
        }
        
        // 排序后比较
        userArr = userArr.map(String).sort()
        correctArr = correctArr.map(String).sort()
        
        console.log('[checkAnswerCorrect] 解析后:', { userArr, correctArr })
        
        if (userArr.length !== correctArr.length) {
          console.log('[checkAnswerCorrect] 长度不同，返回 false')
          return false
        }
        const result = userArr.every((val, idx) => val === correctArr[idx])
        console.log('[checkAnswerCorrect] 比较结果:', result)
        return result
      } catch (e) {
        console.log('[checkAnswerCorrect] 异常，简单比较:', e)
        // 简单字符串比较
        return String(userAnswer).trim() === String(correctAnswer).trim()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.exam-grading-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  overflow: hidden;
}

.grading-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  position: relative;
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #5856D6;
    flex-shrink: 0;
  }
  
  .review-level-tabs {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    
    .level-hint-wrapper {
      min-width: 120px;  // 固定最小宽度，避免内容变化时布局跳动
      display: flex;
      align-items: center;
    }
    
    .level-hint {
      font-size: 12px;
      white-space: nowrap;
      
      i {
        margin-right: 4px;
      }
      
      &.readonly-hint {
        color: #909399;
        background: #f4f4f5;
        padding: 4px 8px;
        border-radius: 4px;
      }
      
      &.warning {
        color: #E6A23C;
      }
    }
  }
}

.grading-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
  flex-shrink: 0;
  
  .readonly-text {
    color: #909399;
    font-size: 14px;
    
    i {
      margin-right: 4px;
    }
  }
}

.grading-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  width: 200px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  
  .panel-header {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .panel-title {
      font-weight: 600;
      color: #303133;
    }
    
    .progress {
      color: #909399;
      font-size: 12px;
    }
  }
  
  .examinee-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }
  
  .examinee-item {
    padding: 10px 16px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    &:hover {
      background: #f5f7fa;
    }
    
    &.active {
      background: #ecf5ff;
      color: #409EFF;
    }
    
    .status-badge {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 10px;
      
      &.status-pending { background: #f0f0f0; color: #909399; }
      &.status-review1 { background: #e6f7ff; color: #1890ff; }
      &.status-review2 { background: #fff7e6; color: #fa8c16; }
      &.status-complete { background: #f6ffed; color: #52c41a; }
    }
  }
  
  .filter-option {
    padding: 12px 16px;
    border-top: 1px solid #f0f0f0;
  }
  
  .examinee-info {
    padding: 16px;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;
    
    .info-row {
      margin-bottom: 8px;
      font-size: 12px;
      
      .label {
        color: #909399;
      }
      .value {
        color: #606266;
      }
    }
  }
}

.center-panel {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  .answer-list {
    max-width: 800px;
    margin: 0 auto;
  }
  
  .question-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  
  .question-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    
    .question-title {
      font-weight: 600;
      color: #303133;
    }
    
    .question-score-info {
      margin-left: 10px;
      color: #909399;
      font-size: 13px;
    }
  }
  
  .question-label {
    margin-bottom: 12px;
    color: #606266;
    line-height: 1.6;
  }
  
  .options-list {
    .option-item {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      margin-bottom: 4px;
      border-radius: 4px;
      position: relative;
      
      &.clickable {
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          background: #ecf5ff;
        }
      }
      
      &.readonly {
        cursor: default;
        opacity: 0.8;
      }
      
      &.is-original-selected {
        background: #fef0f0;
      }
      
      &.is-review-selected {
        background: #ecf5ff;
        border: 2px solid #409EFF;
      }
      
      &.is-correct {
        background: #f0f9eb;
      }
      
      &.is-wrong {
        background: #fef0f0;
      }
      
      .option-marker {
        margin-right: 8px;
        
        .correct-icon {
          color: #67C23A;
        }
        
        .wrong-icon {
          color: #F56C6C;
        }
        
        .review-check {
          color: #409EFF;
          font-weight: bold;
        }
        
        .option-circle {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #dcdfe6;
          border-radius: 50%;
        }
        
        .option-checkbox {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #dcdfe6;
          border-radius: 2px;
        }
      }
      
      .original-tag {
        margin-left: auto;
        padding: 2px 6px;
        font-size: 10px;
        background: #fef0f0;
        color: #F56C6C;
        border-radius: 4px;
      }
      
      .review1-tag {
        margin-left: 4px;
        padding: 2px 6px;
        font-size: 10px;
        background: #e6f7ff;
        color: #1890ff;
        border-radius: 4px;
      }
      
      .review2-tag {
        margin-left: 4px;
        padding: 2px 6px;
        font-size: 10px;
        background: #fff7e6;
        color: #fa8c16;
        border-radius: 4px;
      }
    }
  }
  
  .subjective-answer {
    background: #f5f7fa;
    padding: 12px;
    border-radius: 4px;
    
    .answer-label {
      color: #909399;
      font-size: 12px;
      margin-bottom: 8px;
    }
    
    .answer-content {
      color: #303133;
      line-height: 1.6;
    }
  }
  
  .review-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #e4e7ed;
    
    .review-options {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      
      .review-label {
        color: #606266;
        margin-right: 12px;
        font-weight: 500;
      }
    }
    
    .review-remark {
      margin-top: 8px;
    }
  }
  
  .empty-tip {
    text-align: center;
    padding: 100px 0;
    color: #909399;
  }
}

.right-panel {
  width: 200px;
  background: #fff;
  border-left: 1px solid #e4e7ed;
  padding: 20px;
  display: flex;
  flex-direction: column;
  
  .score-summary {
    text-align: center;
    margin-bottom: 12px;
    
    .label {
      display: block;
      color: #909399;
      font-size: 13px;
      margin-bottom: 4px;
    }
    
    .score {
      font-size: 36px;
      font-weight: 600;
      color: #909399;
      
      &.original {
        color: #606266;
      }
      
      &.changed {
        color: #67C23A;
      }
    }
    
    &.review-score .score {
      color: #F56C6C;
    }
  }
  
  .level-display {
    text-align: center;
    margin-bottom: 20px;
    
    .level-label {
      color: #909399;
      font-size: 12px;
    }
    
    .level-value {
      font-size: 16px;
      font-weight: 600;
      margin-left: 8px;
    }
  }
  
  .question-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
    
    .nav-item {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      
      &.unanswered {
        background: #f5f7fa;
        color: #909399;
      }
      
      &.correct {
        background: #67C23A;
        color: #fff;
      }
      
      &.wrong {
        background: #F56C6C;
        color: #fff;
      }
      
      &.not-applicable {
        background: #909399;
        color: #fff;
      }
    }
  }
  
  .nav-buttons {
    margin-top: auto;
    display: flex;
    gap: 8px;
    
    .el-button {
      flex: 1;
    }
  }
}

// Question 组件适配样式
.question-display-wrapper {
  position: relative;
  
  // 隐藏 Question 组件内的标题（审核页面有自己的标题）
  ::v-deep .question-item {
    .question-header {
      display: none;
    }
    
    .question-desc,
    .question-tip {
      display: none;
    }
  }
}

// 原答案标识样式
.original-answer-markers {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef0f0;
  border-radius: 4px;
  border-left: 3px solid #F56C6C;
  
  .original-answer-marker {
    display: block;
    color: #F56C6C;
    font-size: 13px;
    line-height: 1.6;
    
    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }
}
</style>
